import { useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { clearAgentAccess } from '../components/alliance/agent-access';
import { clearImpersonationRecents } from '../components/alliance/impersonation-search';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { AgentSelect } from '../types';
import { captureError } from '../utils/capture-error';
import { markSessionExpired } from '../utils/session-expired';

/**
 * Sign out, sign in again, and start/stop impersonating, shared by the user
 * menu, the impersonation search, the maintenance banner and the System
 * Screens.
 *
 * ARC keeps the impersonated agent in AppContext only (in memory), so a
 * refresh ends impersonation, as it always has.
 */
export function useSessionActions() {
  // Context
  const { state, dispatch: authDispatch } = React.useContext(AuthContext);
  const appContext = React.useContext(AppContext);
  // Hooks
  const queryClient = useQueryClient();

  const { dispatch } = appContext;
  const impersonatedAgtNo = appContext.state.impersonatedAgent?.agentno;

  /** Drop cached queries whose key mentions this agent's number */
  const removeAgentQueries = React.useCallback(
    (agentNo: string | undefined) => {
      if (agentNo) {
        const needle = `"${agentNo}"`;
        queryClient.removeQueries({
          predicate: (query) => JSON.stringify(query.queryKey).includes(needle),
        });
      }
    },
    [queryClient],
  );

  /**
   * Stop acting as the impersonated agent. Their cached data is removed;
   * queries that aren't agent-scoped (/authorize, MFA, admin lists) are kept.
   */
  const stopImpersonating = React.useCallback(() => {
    dispatch({ type: 'IMPERSONATE_AGENT', payload: null });
    clearAgentAccess(impersonatedAgtNo);
    removeAgentQueries(impersonatedAgtNo);
  }, [dispatch, impersonatedAgtNo, removeAgentQueries]);

  /**
   * Start (or switch) impersonating an agent: drop the previous agent's
   * cached data, then set the new one.
   */
  const startImpersonating = React.useCallback(
    (agent: AgentSelect) => {
      if (impersonatedAgtNo && impersonatedAgtNo !== agent.agentno) {
        removeAgentQueries(impersonatedAgtNo);
      }
      // Ask the API afresh; access may have changed since last time
      clearAgentAccess(impersonatedAgtNo);
      clearAgentAccess(agent.agentno);
      dispatch({ type: 'IMPERSONATE_AGENT', payload: agent });
    },
    [dispatch, impersonatedAgtNo, removeAgentQueries],
  );

  /**
   * Sign out of Cognito, then clear cached data. The gate shows "Signing you
   * out…" from the start, so the app doesn't refetch and flash back while
   * Cognito finishes. Signing out on purpose also forgets the recent
   * impersonations; a session that merely expires keeps them. While the
   * session has expired, the Sign In page is told so it can say so.
   */
  const signOut = React.useCallback(async () => {
    if (state.tokenError) {
      markSessionExpired();
    }
    authDispatch({ type: 'SIGNING_OUT', payload: true });
    try {
      await Auth.signOut();
    } catch (error) {
      captureError({ data: { error } });
      if (state.tokenError) {
        // The session is already gone: go to Sign In regardless
        queryClient.removeQueries();
        window.location.assign('/sign-in?reason=expired');
      } else {
        // Still signed in: let them back into the app
        authDispatch({ type: 'SIGNING_OUT', payload: false });
      }
      return;
    }
    dispatch({ type: 'IMPERSONATE_AGENT', payload: null });
    clearImpersonationRecents();
    queryClient.removeQueries();
  }, [authDispatch, dispatch, queryClient, state.tokenError]);

  /**
   * Back to the Cognito Hosted UI after the session expired. Cognito may
   * sign them straight back in if its own session is still alive.
   */
  const signInAgain = React.useCallback(async () => {
    try {
      await Auth.federatedSignIn();
    } catch (error) {
      captureError({ data: { error } });
      window.location.assign('/sign-in?reason=expired');
    }
  }, []);

  return { signOut, stopImpersonating, startImpersonating, signInAgain };
}
